import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useAuth } from "../context/AuthContext";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import { fetchZoneGroupsForPicker, type ZoneGroupOption } from "../api/projectInitiationApi";
import { applicationMatchesGeoZone } from "../utils/geoZones";
import {
  fetchIssuePoAnalytics,
  fetchIssuePoBlockedMilestones,
  fetchIssuePoItems,
  fetchIssuePoRemainingQtyByItem,
  fetchIssuePoRegister,
  fetchIssueMaterialAnalytics,
  fetchIssueMaterialRegister,
  cancelIssuePo,
  generateIssuePo,
  generateIssueMaterialRows,
  generateIssueMaterialFromPo,
  fetchOpenPoIdsForIssue,
  fetchPoIssueBalance,
  type IssuePoAnalytics,
  type IssuePoBlockedMilestoneGroup,
  type IssuePoHeader,
  type IssuePoItem,
  type IssueMaterialAnalytics,
  type IssueMaterialRow,
  type IssueMaterialStatus,
  type IssuePoShipToInput,
  type PoIssueBalanceLine,
} from "../api/mrrInventoryApi";
import { PoGenerateShipToModal, type PoShipToChoice } from "../components/PoGenerateShipToModal";
import { parseQuantityInput, sanitizeQtyFieldInput } from "../utils/qtyInput";
import { PoGenerateSuccessTicket } from "../components/PoGenerateSuccessTicket";
import { downloadIssuePoPdf } from "../utils/issuePoPdf";

/** Trim trailing zeros for qty input display (matches typical PO decimals). */
function formatQtyInputDisplay(n: number): string {
  if (!Number.isFinite(n)) return "0";
  const s = n.toFixed(4).replace(/\.?0+$/, "");
  return s || "0";
}

/**
 * Step 3 default qty: remaining allowed after earlier POs for this milestone (when API loaded),
 * otherwise master planned qty until the check finishes.
 */
function poLineBaseQtyForRemaining(args: {
  masterQty: number;
  itemId: string;
  remainingByItem: Record<string, number>;
  remainingLoading: boolean;
}): number {
  const { masterQty, itemId, remainingByItem, remainingLoading } = args;
  if (remainingLoading) return masterQty;
  const r = remainingByItem[itemId];
  if (r === undefined) return masterQty;
  return r;
}

type SelectionMode = "all" | "selected";
type RegisterSection = "po" | "issue";
type PoViewMode = "generate" | "register";
type IssueViewMode = "generate" | "register";
type IssueGenerateSubMode = "manual" | "fromPo";

function formatWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function fmtInrAmount(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type IssueLineState = "none" | "open" | "completed";

function issueRowLookupKey(applicationId: string, milestoneTemplateId: string, standardResourceItemId: string) {
  return `${applicationId}\t${milestoneTemplateId}\t${standardResourceItemId}`;
}

function issueLineState(row?: IssueMaterialRow): IssueLineState {
  if (!row) return "none";
  if (row.status === "completed" || row.balanceQty <= 0) return "completed";
  return "open";
}

function issueLineStateLabel(state: IssueLineState): string {
  if (state === "completed") return "Completed";
  if (state === "open") return "Open";
  return "Not created";
}

function evaluateIssueGeneration(lines: IssueLineState[]) {
  if (lines.length === 0) {
    return { canGenerate: false, message: "", showMessage: false };
  }
  const hasNone = lines.some((s) => s === "none");
  const allCompleted = lines.every((s) => s === "completed");
  const allExist = lines.every((s) => s !== "none");
  if (hasNone) return { canGenerate: true, message: "", showMessage: false };
  if (allCompleted) {
    return {
      canGenerate: false,
      showMessage: true,
      message:
        "Material issue is already completed for this selection. No new issue rows can be generated. View records in the Register tab.",
    };
  }
  if (allExist) {
    return {
      canGenerate: false,
      showMessage: true,
      message:
        "Issue rows already exist for these items. Use Inventory outward to dispatch remaining stock.",
    };
  }
  return { canGenerate: true, message: "", showMessage: false };
}

const STATUS_OPTIONS: Array<{ id: "all" | IssueMaterialStatus; label: string }> = [
  { id: "all", label: "All statuses" },
  { id: "pending", label: "Pending" },
  { id: "partial", label: "Partial" },
  { id: "completed", label: "Completed" },
];

export function IssueMaterialRegisterPage({ section = "issue" }: { section?: RegisterSection }) {
  const { can } = useAuth();
  const { applications, milestoneTemplates, standardResources, subcontractorMasters, geoZones, storeYards } =
    useAppState();
  const { showToast } = useToast();

  // Zone groups for filtering (loaded from API, same as ProjectInitiationPage)
  const [zoneGroupOptions, setZoneGroupOptions] = useState<ZoneGroupOption[]>([]);
  useEffect(() => {
    fetchZoneGroupsForPicker().then(setZoneGroupOptions).catch(() => {/* silent */});
  }, []);
  const canUsePo = can("inventory_issue_po");
  const isPoSection = section === "po";
  const isIssueSection = section === "issue";
  const [poViewMode, setPoViewMode] = useState<PoViewMode>("generate");
  const [issueViewMode, setIssueViewMode] = useState<IssueViewMode>("generate");

  const [applicationId, setApplicationId] = useState("");
  const [milestoneTemplateId, setMilestoneTemplateId] = useState("");
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("all");
  const [issueBeneficiarySearch, setIssueBeneficiarySearch] = useState("");
  const [issueDistrictFilter, setIssueDistrictFilter] = useState<string>("all");
  const [issueMandalFilter, setIssueMandalFilter] = useState<string>("all");
  const [issueVillageFilter, setIssueVillageFilter] = useState<string>("all");
  const [issueZoneGroupFilter, setIssueZoneGroupFilter] = useState<string>("all");
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [issueGenerateSubMode, setIssueGenerateSubMode] = useState<IssueGenerateSubMode>("manual");
  const [issueFromPoId, setIssueFromPoId] = useState("");
  const [issueFromPoItems, setIssueFromPoItems] = useState<IssuePoItem[]>([]);
  const [issueFromPoItemsLoading, setIssueFromPoItemsLoading] = useState(false);
  const [issueFromPoNotes, setIssueFromPoNotes] = useState("");
  const [generatingFromPo, setGeneratingFromPo] = useState(false);
  const [issueFromPoMode, setIssueFromPoMode] = useState<"full" | "partial">("full");
  const [issueFromPoQtyOverrides, setIssueFromPoQtyOverrides] = useState<Record<string, string>>({});
  const [openPoIdsForIssue, setOpenPoIdsForIssue] = useState<string[]>([]);
  const [openPoIdsForIssueLoading, setOpenPoIdsForIssueLoading] = useState(false);
  const [issueFromPoBalance, setIssueFromPoBalance] = useState<PoIssueBalanceLine[]>([]);
  const [issueFromPoBalanceLoading, setIssueFromPoBalanceLoading] = useState(false);

  const [rows, setRows] = useState<IssueMaterialRow[]>([]);
  const [allIssueRows, setAllIssueRows] = useState<IssueMaterialRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | IssueMaterialStatus>("all");
  const [filterSearch, setFilterSearch] = useState("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [poApplicationIds, setPoApplicationIds] = useState<string[]>([]);
  const [poMilestoneTemplateId, setPoMilestoneTemplateId] = useState("");
  const [poSelectionMode, setPoSelectionMode] = useState<"full" | "partial">("full");
  const [poBeneficiarySearch, setPoBeneficiarySearch] = useState("");
  const [poDistrictFilter, setPoDistrictFilter] = useState<string>("all");
  const [poMandalFilter, setPoMandalFilter] = useState<string>("all");
  const [poVillageFilter, setPoVillageFilter] = useState<string>("all");
  const [poZoneGroupFilter, setPoZoneGroupFilter] = useState<string>("all");
  const [poShowSelectedOnly, setPoShowSelectedOnly] = useState(false);
  const [poSelectedResourceIds, setPoSelectedResourceIds] = useState<string[]>([]);
  const [poSelectedResourceItemIds, setPoSelectedResourceItemIds] = useState<string[]>([]);
  const [poQtyOverrides, setPoQtyOverrides] = useState<Record<string, string>>({});
  const [poGenerating, setPoGenerating] = useState(false);
  const [poShipToModalOpen, setPoShipToModalOpen] = useState(false);
  const [poRows, setPoRows] = useState<IssuePoHeader[]>([]);
  const [poRowsLoading, setPoRowsLoading] = useState(false);
  const [poRowsError, setPoRowsError] = useState<string | null>(null);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [selectedPoItems, setSelectedPoItems] = useState<IssuePoItem[]>([]);
  const [poItemsLoading, setPoItemsLoading] = useState(false);
  const [poPdfDownloadingId, setPoPdfDownloadingId] = useState("");
  const [poCancellingId, setPoCancellingId] = useState("");
  /** Milestones where any selected beneficiary already has a full PO — omitted from Step 2 dropdown. */
  const [poBlockedMilestoneTemplateIds, setPoBlockedMilestoneTemplateIds] = useState<string[]>([]);
  const [poBlockedFullPos, setPoBlockedFullPos] = useState<IssuePoBlockedMilestoneGroup[]>([]);
  const [poBlockedMilestonesLoading, setPoBlockedMilestonesLoading] = useState(false);
  const [poGenerateSuccess, setPoGenerateSuccess] = useState<{
    po: IssuePoHeader;
    items: IssuePoItem[];
  } | null>(null);
  /** Min remaining qty per standard resource item across selected beneficiaries (from API). */
  const [poRemainingQtyByItemId, setPoRemainingQtyByItemId] = useState<Record<string, number>>({});
  const [poRemainingQtyLoading, setPoRemainingQtyLoading] = useState(false);

  const [poAnalyticsData, setPoAnalyticsData] = useState<IssuePoAnalytics | null>(null);
  const [poAnalyticsLoading, setPoAnalyticsLoading] = useState(false);
  const [poAnalyticsError, setPoAnalyticsError] = useState<string | null>(null);
  const [issueAnalyticsData, setIssueAnalyticsData] = useState<IssueMaterialAnalytics | null>(null);
  const [issueAnalyticsLoading, setIssueAnalyticsLoading] = useState(false);
  const [issueAnalyticsError, setIssueAnalyticsError] = useState<string | null>(null);

  // All applications that have a subcontractor linked
  const subcontractorLinked = useMemo(
    () => applications.filter((a) => Boolean(a.subcontractor.subcontractorMasterId)),
    [applications],
  );
  // Eligible for PO: must ALSO have a project initiation (plannedStartDate set)
  const eligibleApplications = useMemo(
    () =>
      subcontractorLinked
        .filter((a) => Boolean(a.plannedStartDate))
        .sort((a, b) => a.mobile.beneficiaryName.localeCompare(b.mobile.beneficiaryName)),
    [subcontractorLinked],
  );
  // Blocked from PO: subcontractor linked but no project initiation yet
  const notInitiatedApplications = useMemo(
    () =>
      subcontractorLinked
        .filter((a) => !a.plannedStartDate)
        .sort((a, b) => a.mobile.beneficiaryName.localeCompare(b.mobile.beneficiaryName)),
    [subcontractorLinked],
  );

  const appById = useMemo(() => new Map(applications.map((a) => [a.id, a])), [applications]);

  const selectedMilestone = useMemo(
    () => milestoneTemplates.find((m) => m.id === milestoneTemplateId),
    [milestoneTemplateId, milestoneTemplates],
  );
  const selectedPoMilestone = useMemo(
    () => milestoneTemplates.find((m) => m.id === poMilestoneTemplateId),
    [poMilestoneTemplateId, milestoneTemplates],
  );

  const linkedResources = useMemo(() => {
    if (!selectedMilestone) return [];
    const set = new Set(selectedMilestone.linkedResourceIds);
    return standardResources
      .filter((r) => set.has(r.id))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [selectedMilestone, standardResources]);
  const poLinkedResources = useMemo(() => {
    if (!selectedPoMilestone) return [];
    const set = new Set(selectedPoMilestone.linkedResourceIds);
    return standardResources.filter((r) => set.has(r.id)).sort((a, b) => a.title.localeCompare(b.title));
  }, [selectedPoMilestone, standardResources]);

  const pickedResourceIds = useMemo(
    () => (selectionMode === "all" ? linkedResources.map((r) => r.id) : selectedResourceIds),
    [selectionMode, linkedResources, selectedResourceIds],
  );

  const previewItems = useMemo(() => {
    const picked = new Set(pickedResourceIds);
    return linkedResources
      .filter((r) => picked.has(r.id))
      .flatMap((resource) =>
        resource.items.map((item) => ({
          resourceId: resource.id,
          resourceTitle: resource.title,
          itemId: item.id,
          itemLabel: item.label,
          qty: item.quantity,
          unit: item.unit,
          rate: item.rate ?? 0,
          hsnCode: item.hsnCode ?? "",
          catalogStandardItemId: item.catalogStandardItemId ?? "",
        })),
      );
  }, [linkedResources, pickedResourceIds]);

  const hasMissingCatalog = useMemo(
    () => previewItems.some((it) => !it.catalogStandardItemId.trim()),
    [previewItems],
  );
  const poPreviewItems = useMemo(() => {
    const pickedResources =
      poSelectionMode === "full"
        ? new Set(poLinkedResources.map((r) => r.id))
        : new Set(poSelectedResourceIds);
    let items = poLinkedResources
      .filter((r) => pickedResources.has(r.id))
      .flatMap((resource) =>
        resource.items.map((item) => ({
          resourceId: resource.id,
          resourceTitle: resource.title,
          itemId: item.id,
          itemLabel: item.label,
          qty: item.quantity,
          unit: item.unit,
          rate: item.rate ?? 0,
          hsnCode: item.hsnCode ?? "",
          catalogStandardItemId: item.catalogStandardItemId ?? "",
        })),
      );
    if (poSelectionMode === "partial" && poSelectedResourceItemIds.length > 0) {
      const selected = new Set(poSelectedResourceItemIds);
      items = items.filter((it) => selected.has(it.itemId));
    }
    return items;
  }, [poLinkedResources, poSelectedResourceIds, poSelectedResourceItemIds, poSelectionMode]);

  const poHasMissingCatalog = useMemo(
    () => poPreviewItems.some((it) => !it.catalogStandardItemId.trim()),
    [poPreviewItems],
  );
  const poPreviewWithQty = useMemo(
    () =>
      poPreviewItems.map((it) => {
        const baseQty = poLineBaseQtyForRemaining({
          masterQty: it.qty,
          itemId: it.itemId,
          remainingByItem: poRemainingQtyByItemId,
          remainingLoading: poRemainingQtyLoading,
        });
        const typed = poQtyOverrides[it.itemId];
        const parsed =
          typed == null || String(typed).trim() === "" ? NaN : parseQuantityInput(typed);
        const effectiveQty = parsed > 0 ? parsed : baseQty;
        const rate = it.rate ?? 0;
        const lineAmount = Math.round(effectiveQty * rate * 100) / 100;
        return {
          ...it,
          effectiveQty,
          qtyInputValue: typed != null ? typed : formatQtyInputDisplay(baseQty),
          lineAmount,
        };
      }),
    [poPreviewItems, poQtyOverrides, poRemainingQtyByItemId, poRemainingQtyLoading],
  );

  const poSubcontractorValidation = useMemo(() => {
    if (!poApplicationIds.length) return { ok: true, subcontractorId: "" };
    const subIds = new Set(
      poApplicationIds
        .map((id) => appById.get(id)?.subcontractor?.subcontractorMasterId ?? "")
        .filter((x) => String(x).trim()),
    );
    if (subIds.size !== 1) return { ok: false, subcontractorId: "" };
    return { ok: true, subcontractorId: [...subIds][0] };
  }, [appById, poApplicationIds]);
  const poDistrictOptions = useMemo(() => {
    const districts = [...new Set(eligibleApplications.map((a) => String(a.district).trim()).filter(Boolean))];
    districts.sort((a, b) => a.localeCompare(b));
    return districts;
  }, [eligibleApplications]);
  const issueDistrictOptions = useMemo(() => {
    const districts = [...new Set(eligibleApplications.map((a) => String(a.district).trim()).filter(Boolean))];
    districts.sort((a, b) => a.localeCompare(b));
    return districts;
  }, [eligibleApplications]);
  const issueMandalOptions = useMemo(() => {
    const list = eligibleApplications
      .filter((a) => issueDistrictFilter === "all" || a.district === issueDistrictFilter)
      .map((a) => String(a.mandal).trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [eligibleApplications, issueDistrictFilter]);
  const issueVillageOptions = useMemo(() => {
    const list = eligibleApplications
      .filter((a) => issueDistrictFilter === "all" || a.district === issueDistrictFilter)
      .filter((a) => issueMandalFilter === "all" || a.mandal === issueMandalFilter)
      .map((a) => String(a.village).trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [eligibleApplications, issueDistrictFilter, issueMandalFilter]);
  const filteredIssueBeneficiaries = useMemo(() => {
    const q = issueBeneficiarySearch.trim().toLowerCase();
    return eligibleApplications.filter((a) => {
      if (issueDistrictFilter !== "all" && a.district !== issueDistrictFilter) return false;
      if (issueMandalFilter !== "all" && a.mandal !== issueMandalFilter) return false;
      if (issueVillageFilter !== "all" && a.village !== issueVillageFilter) return false;
      if (issueZoneGroupFilter !== "all") {
        const grpOption = zoneGroupOptions.find((g) => g.id === issueZoneGroupFilter);
        if (grpOption) {
          const matchingZone = geoZones.find((z) => z.id === grpOption.zoneId);
          if (!matchingZone || !applicationMatchesGeoZone(a, matchingZone)) return false;
        }
      }
      if (!q) return true;
      const name = a.mobile.beneficiaryName.toLowerCase();
      const appNo = a.mobile.applicationId.toLowerCase();
      return name.includes(q) || appNo.includes(q);
    });
  }, [eligibleApplications, issueBeneficiarySearch, issueDistrictFilter, issueMandalFilter, issueVillageFilter, issueZoneGroupFilter, zoneGroupOptions, geoZones]);
  const poMandalOptions = useMemo(() => {
    const list = eligibleApplications
      .filter((a) => poDistrictFilter === "all" || a.district === poDistrictFilter)
      .map((a) => String(a.mandal).trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [eligibleApplications, poDistrictFilter]);
  const poVillageOptions = useMemo(() => {
    const list = eligibleApplications
      .filter((a) => poDistrictFilter === "all" || a.district === poDistrictFilter)
      .filter((a) => poMandalFilter === "all" || a.mandal === poMandalFilter)
      .map((a) => String(a.village).trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [eligibleApplications, poDistrictFilter, poMandalFilter]);
  const filteredPoBeneficiaries = useMemo(() => {
    const q = poBeneficiarySearch.trim().toLowerCase();
    return eligibleApplications.filter((a) => {
      if (poDistrictFilter !== "all" && a.district !== poDistrictFilter) return false;
      if (poMandalFilter !== "all" && a.mandal !== poMandalFilter) return false;
      if (poVillageFilter !== "all" && a.village !== poVillageFilter) return false;
      if (poZoneGroupFilter !== "all") {
        const grpOption = zoneGroupOptions.find((g) => g.id === poZoneGroupFilter);
        if (grpOption) {
          const matchingZone = geoZones.find((z) => z.id === grpOption.zoneId);
          if (!matchingZone || !applicationMatchesGeoZone(a, matchingZone)) return false;
        }
      }
      if (!q) return true;
      const name = a.mobile.beneficiaryName.toLowerCase();
      const appNo = a.mobile.applicationId.toLowerCase();
      return name.includes(q) || appNo.includes(q);
    });
  }, [eligibleApplications, poBeneficiarySearch, poDistrictFilter, poMandalFilter, poVillageFilter, poZoneGroupFilter, zoneGroupOptions, geoZones]);
  const visiblePoBeneficiaries = useMemo(
    () =>
      poShowSelectedOnly
        ? filteredPoBeneficiaries.filter((a) => poApplicationIds.includes(a.id))
        : filteredPoBeneficiaries,
    [filteredPoBeneficiaries, poApplicationIds, poShowSelectedOnly],
  );
  const poSelectedBeneficiaryNames = useMemo(
    () =>
      poApplicationIds
        .map((id) => appById.get(id)?.mobile?.beneficiaryName)
        .filter((x): x is string => Boolean(x)),
    [appById, poApplicationIds],
  );
  const subcontractorNameById = useMemo(
    () => new Map(subcontractorMasters.map((s) => [s.id, s.firmName])),
    [subcontractorMasters],
  );
  const visiblePoBeneficiaryIds = useMemo(() => visiblePoBeneficiaries.map((a) => a.id), [visiblePoBeneficiaries]);
  const poAllVisibleSelected = useMemo(
    () => visiblePoBeneficiaryIds.length > 0 && visiblePoBeneficiaryIds.every((id) => poApplicationIds.includes(id)),
    [visiblePoBeneficiaryIds, poApplicationIds],
  );
  const poDistrictSelectOptions = useMemo(
    () => [{ value: "all", label: "All districts" }, ...poDistrictOptions.map((d) => ({ value: d, label: d }))],
    [poDistrictOptions],
  );
  const poMandalSelectOptions = useMemo(
    () => [{ value: "all", label: "All mandals" }, ...poMandalOptions.map((m) => ({ value: m, label: m }))],
    [poMandalOptions],
  );
  const poVillageSelectOptions = useMemo(
    () => [{ value: "all", label: "All villages" }, ...poVillageOptions.map((v) => ({ value: v, label: v }))],
    [poVillageOptions],
  );
  const zoneGroupsByCategory = useMemo(
    () =>
      zoneGroupOptions.reduce<Record<string, ZoneGroupOption[]>>((acc, g) => {
        const cat = g.categoryName || "General";
        (acc[cat] ??= []).push(g);
        return acc;
      }, {}),
    [zoneGroupOptions],
  );
  const poZoneGroupSelectOptions = useMemo(() => {
    const opts: { value: string; label: string; group?: string }[] = [{ value: "all", label: "All groups" }];
    for (const [cat, grps] of Object.entries(zoneGroupsByCategory)) {
      for (const g of grps) opts.push({ value: g.id, label: `${cat}: ${g.name}`, group: cat });
    }
    return opts;
  }, [zoneGroupsByCategory]);
  const issueZoneGroupSelectOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: "all", label: "All groups" }];
    for (const [cat, grps] of Object.entries(zoneGroupsByCategory)) {
      for (const g of grps) opts.push({ value: g.id, label: `${cat}: ${g.name}` });
    }
    return opts;
  }, [zoneGroupsByCategory]);
  const issueDistrictSelectOptions = useMemo(
    () => [{ value: "all", label: "All districts" }, ...issueDistrictOptions.map((d) => ({ value: d, label: d }))],
    [issueDistrictOptions],
  );
  const issueMandalSelectOptions = useMemo(
    () => [{ value: "all", label: "All mandals" }, ...issueMandalOptions.map((m) => ({ value: m, label: m }))],
    [issueMandalOptions],
  );
  const issueVillageSelectOptions = useMemo(
    () => [{ value: "all", label: "All villages" }, ...issueVillageOptions.map((v) => ({ value: v, label: v }))],
    [issueVillageOptions],
  );
  const issueBeneficiarySelectOptions = useMemo(
    () => [
      { value: "", label: filteredIssueBeneficiaries.length ? "Select…" : "No beneficiaries match filters" },
      ...filteredIssueBeneficiaries.map((a) => ({
        value: a.id,
        label: `${a.mobile.beneficiaryName} — ${a.mobile.applicationId}`,
      })),
    ],
    [filteredIssueBeneficiaries],
  );
  const issueModeSelectOptions = useMemo(
    () => [
      { value: "all", label: "Entire milestone resources" },
      { value: "selected", label: "Selected resource(s)" },
    ],
    [],
  );
  const poBlockedMilestoneIdSet = useMemo(
    () => new Set(poBlockedMilestoneTemplateIds),
    [poBlockedMilestoneTemplateIds],
  );
  const poMilestoneSelectOptions = useMemo(() => {
    const templates =
      poApplicationIds.length > 0 && !poBlockedMilestonesLoading
        ? milestoneTemplates.filter((m) => !poBlockedMilestoneIdSet.has(m.id))
        : milestoneTemplates;
    return [{ value: "", label: "Select…" }, ...templates.map((m) => ({ value: m.id, label: m.title }))];
  }, [milestoneTemplates, poApplicationIds.length, poBlockedMilestonesLoading, poBlockedMilestoneIdSet]);
  const poModeSelectOptions = useMemo(
    () => [
      { value: "full", label: "Complete PO (all linked resources/items)" },
      { value: "partial", label: "Partial PO (selected resources/items)" },
    ],
    [],
  );
  const poPreviewHasExhaustedQtyLine = useMemo(() => {
    if (poRemainingQtyLoading) return false;
    return poPreviewItems.some((it) => {
      const r = poRemainingQtyByItemId[it.itemId];
      return r !== undefined && r <= 0;
    });
  }, [poPreviewItems, poRemainingQtyByItemId, poRemainingQtyLoading]);

  const poCanGenerate = useMemo(() => {
    if (!poApplicationIds.length) return { ok: false, reason: "Select at least one beneficiary." };
    if (!poMilestoneTemplateId.trim()) return { ok: false, reason: "Select milestone." };
    if (poApplicationIds.length > 0 && poBlockedMilestonesLoading) {
      return { ok: false, reason: "Updating milestone list for selected beneficiaries…" };
    }
    if (poApplicationIds.length > 0 && poMilestoneTemplateId.trim() && poRemainingQtyLoading) {
      return { ok: false, reason: "Checking how much quantity is still allowed per line…" };
    }
    if (!poSubcontractorValidation.ok) return { ok: false, reason: "Selected beneficiaries must have same subcontractor." };
    if (poSelectionMode === "partial" && poSelectedResourceIds.length === 0 && poSelectedResourceItemIds.length === 0) {
      return { ok: false, reason: "For partial PO, select resources or specific items." };
    }
    if (!poPreviewItems.length) return { ok: false, reason: "No valid items selected for PO." };
    if (poHasMissingCatalog) return { ok: false, reason: "Some selected items are not linked to Item Master." };
    if (poPreviewHasExhaustedQtyLine) {
      return {
        ok: false,
        reason:
          "One or more lines have no remaining quantity (already fully PO’d) — remove those lines or change beneficiaries.",
      };
    }
    return { ok: true, reason: "" };
  }, [
    poApplicationIds,
    poMilestoneTemplateId,
    poSubcontractorValidation.ok,
    poSelectionMode,
    poSelectedResourceIds.length,
    poSelectedResourceItemIds.length,
    poPreviewItems.length,
    poHasMissingCatalog,
    poBlockedMilestonesLoading,
    poRemainingQtyLoading,
    poPreviewHasExhaustedQtyLine,
  ]);

  useEffect(() => {
    if (!canUsePo || !isPoSection || poViewMode !== "generate") return;
    if (!poApplicationIds.length) {
      setPoBlockedMilestoneTemplateIds([]);
      setPoBlockedFullPos([]);
      setPoBlockedMilestonesLoading(false);
      return;
    }
    let cancelled = false;
    setPoBlockedMilestonesLoading(true);
    void fetchIssuePoBlockedMilestones(poApplicationIds)
      .then((data) => {
        if (!cancelled) {
          setPoBlockedMilestoneTemplateIds(data.milestoneTemplateIds ?? []);
          setPoBlockedFullPos(data.blockedFullPos ?? []);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setPoBlockedMilestoneTemplateIds([]);
          setPoBlockedFullPos([]);
          showToast(e instanceof Error ? e.message : "Could not load milestone eligibility for beneficiaries.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setPoBlockedMilestonesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUsePo, isPoSection, poViewMode, poApplicationIds, showToast]);

  useEffect(() => {
    if (!canUsePo || !isPoSection || poViewMode !== "generate") return;
    const mid = poMilestoneTemplateId.trim();
    if (!mid || !poApplicationIds.length) {
      setPoRemainingQtyByItemId({});
      setPoRemainingQtyLoading(false);
      return;
    }
    let cancelled = false;
    setPoRemainingQtyLoading(true);
    void fetchIssuePoRemainingQtyByItem({ milestoneTemplateId: mid, applicationIds: poApplicationIds })
      .then((data) => {
        if (!cancelled) setPoRemainingQtyByItemId(data.remainingQtyByStandardResourceItemId ?? {});
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setPoRemainingQtyByItemId({});
          showToast(
            e instanceof Error ? e.message : "Could not load remaining PO quantities for selected beneficiaries.",
            "error",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPoRemainingQtyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUsePo, isPoSection, poViewMode, poMilestoneTemplateId, poApplicationIds, showToast]);

  useEffect(() => {
    const blocked = new Set(poBlockedMilestoneTemplateIds);
    if (poMilestoneTemplateId && blocked.has(poMilestoneTemplateId)) {
      setPoMilestoneTemplateId("");
    }
  }, [poMilestoneTemplateId, poBlockedMilestoneTemplateIds]);

  useEffect(() => {
    setPoMandalFilter("all");
    setPoVillageFilter("all");
    setPoZoneGroupFilter("all");
  }, [poDistrictFilter]);
  useEffect(() => {
    setPoVillageFilter("all");
  }, [poMandalFilter]);
  useEffect(() => {
    setIssueMandalFilter("all");
    setIssueVillageFilter("all");
    setIssueZoneGroupFilter("all");
  }, [issueDistrictFilter]);
  useEffect(() => {
    setIssueVillageFilter("all");
  }, [issueMandalFilter]);
  // When zone group is picked, reset district/mandal/village (mutually exclusive quick-filter)
  useEffect(() => {
    if (poZoneGroupFilter !== "all") {
      setPoDistrictFilter("all");
      setPoMandalFilter("all");
      setPoVillageFilter("all");
    }
  }, [poZoneGroupFilter]);
  useEffect(() => {
    if (issueZoneGroupFilter !== "all") {
      setIssueDistrictFilter("all");
      setIssueMandalFilter("all");
      setIssueVillageFilter("all");
    }
  }, [issueZoneGroupFilter]);
  useEffect(() => {
    if (!canUsePo && issueGenerateSubMode === "fromPo") setIssueGenerateSubMode("manual");
  }, [canUsePo, issueGenerateSubMode]);

  useEffect(() => {
    if (!applicationId) return;
    const validIds = new Set(filteredIssueBeneficiaries.map((a) => a.id));
    if (!validIds.has(applicationId)) setApplicationId("");
  }, [applicationId, filteredIssueBeneficiaries]);

  useEffect(() => {
    setSelectedResourceIds([]);
  }, [milestoneTemplateId]);
  useEffect(() => {
    setPoSelectedResourceIds([]);
    setPoSelectedResourceItemIds([]);
    setPoQtyOverrides({});
  }, [poMilestoneTemplateId]);
  useEffect(() => {
    setPoQtyOverrides((prev) => {
      const next = { ...prev };
      const valid = new Set(poPreviewItems.map((it) => it.itemId));
      for (const key of Object.keys(next)) {
        if (!valid.has(key)) delete next[key];
      }
      return next;
    });
  }, [poPreviewItems]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    setRowsError(null);
    try {
      const list = await fetchIssueMaterialRegister({
        limit: 1500,
        status: filterStatus === "all" ? undefined : filterStatus,
        search: filterSearch.trim() || undefined,
        sort: sortDir,
      });
      setRows(list);
    } catch (e) {
      setRows([]);
      setRowsError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingRows(false);
    }
  }, [filterSearch, filterStatus, sortDir]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const loadAllIssueRows = useCallback(async () => {
    try {
      const list = await fetchIssueMaterialRegister({ limit: 2000, sort: "desc" });
      setAllIssueRows(list);
    } catch {
      setAllIssueRows([]);
    }
  }, []);

  useEffect(() => {
    if (isIssueSection && issueViewMode === "generate") {
      void loadAllIssueRows();
    }
  }, [isIssueSection, issueViewMode, loadAllIssueRows]);

  const issueRowByKey = useMemo(() => {
    const map = new Map<string, IssueMaterialRow>();
    for (const row of allIssueRows) {
      map.set(
        issueRowLookupKey(row.applicationId, row.milestoneTemplateId, row.standardResourceItemId),
        row,
      );
    }
    return map;
  }, [allIssueRows]);

  const manualIssueGenerateState = useMemo(() => {
    if (!applicationId.trim() || !milestoneTemplateId.trim() || previewItems.length === 0) {
      return { canGenerate: false, message: "", showMessage: false };
    }
    const app = applicationId.trim();
    const ms = milestoneTemplateId.trim();
    const lineStates = previewItems.map((it) =>
      issueLineState(issueRowByKey.get(issueRowLookupKey(app, ms, it.itemId))),
    );
    return evaluateIssueGeneration(lineStates);
  }, [applicationId, milestoneTemplateId, previewItems, issueRowByKey]);

  const issueFromPoBalanceByItemId = useMemo(() => {
    const map = new Map<string, PoIssueBalanceLine>();
    for (const line of issueFromPoBalance) {
      map.set(line.poItemId, line);
    }
    return map;
  }, [issueFromPoBalance]);

  const loadOpenPoIdsForIssue = useCallback(async () => {
    setOpenPoIdsForIssueLoading(true);
    try {
      const ids = await fetchOpenPoIdsForIssue();
      setOpenPoIdsForIssue(ids);
    } catch {
      setOpenPoIdsForIssue([]);
    } finally {
      setOpenPoIdsForIssueLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canUsePo || !isIssueSection || issueViewMode !== "generate" || issueGenerateSubMode !== "fromPo") return;
    void loadOpenPoIdsForIssue();
  }, [canUsePo, isIssueSection, issueViewMode, issueGenerateSubMode, loadOpenPoIdsForIssue]);

  useEffect(() => {
    if (!issueFromPoId.trim()) {
      setIssueFromPoBalance([]);
      return;
    }
    let cancelled = false;
    setIssueFromPoBalanceLoading(true);
    void fetchPoIssueBalance(issueFromPoId.trim())
      .then((data) => {
        if (!cancelled) setIssueFromPoBalance(data.items);
      })
      .catch(() => {
        if (!cancelled) setIssueFromPoBalance([]);
      })
      .finally(() => {
        if (!cancelled) setIssueFromPoBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [issueFromPoId]);

  useEffect(() => {
    if (issueFromPoMode !== "full") return;
    const next: Record<string, string> = {};
    for (const it of issueFromPoItems) {
      const bal = issueFromPoBalanceByItemId.get(it.id);
      const yetTo = bal?.yetToIssue ?? it.plannedQty;
      if (yetTo > 0) next[it.id] = formatQtyInputDisplay(yetTo);
    }
    setIssueFromPoQtyOverrides(next);
  }, [issueFromPoMode, issueFromPoItems, issueFromPoBalanceByItemId]);

  useEffect(() => {
    setIssueFromPoQtyOverrides({});
    setIssueFromPoMode("full");
  }, [issueFromPoId]);

  const issueFromPoGenerateState = useMemo(() => {
    if (!issueFromPoId.trim() || issueFromPoItemsLoading || issueFromPoBalanceLoading) {
      return { canGenerate: false, message: "", showMessage: false };
    }
    if (issueFromPoItems.length === 0) {
      return { canGenerate: false, message: "", showMessage: false };
    }
    const hasOpen = issueFromPoBalance.some((l) => l.yetToIssue > 0);
    if (!hasOpen) {
      return {
        canGenerate: false,
        showMessage: true,
        message: "This PO is fully issued. No remaining PO quantity to issue.",
      };
    }

    let totalIssueQty = 0;
    for (const it of issueFromPoItems) {
      const bal = issueFromPoBalanceByItemId.get(it.id);
      const yetTo = bal?.yetToIssue ?? 0;
      if (!(yetTo > 0)) continue;
      let issueQty: number;
      if (issueFromPoMode === "full") {
        issueQty = yetTo;
      } else {
        const raw = issueFromPoQtyOverrides[it.id];
        if (raw == null || String(raw).trim() === "") continue;
        issueQty = parseQuantityInput(raw);
        if (!(issueQty > 0)) {
          return {
            canGenerate: false,
            showMessage: true,
            message: `Enter a quantity greater than zero for "${it.resourceItemLabel}" or leave it blank.`,
          };
        }
        if (issueQty - yetTo > 0.000001) {
          return {
            canGenerate: false,
            showMessage: true,
            message: `Issue quantity for "${it.resourceItemLabel}" exceeds yet to issue (${formatQtyInputDisplay(yetTo)}).`,
          };
        }
      }
      totalIssueQty += issueQty;
    }

    if (!(totalIssueQty > 0)) {
      return {
        canGenerate: false,
        showMessage: true,
        message:
          issueFromPoMode === "partial"
            ? "Enter issue quantity for at least one line with remaining PO balance."
            : "No remaining PO quantity to issue on the selected lines.",
      };
    }
    return { canGenerate: true, message: "", showMessage: false };
  }, [
    issueFromPoId,
    issueFromPoItems,
    issueFromPoItemsLoading,
    issueFromPoBalanceLoading,
    issueFromPoBalance,
    issueFromPoBalanceByItemId,
    issueFromPoMode,
    issueFromPoQtyOverrides,
  ]);

  const loadPoRows = useCallback(async () => {
    setPoRowsLoading(true);
    setPoRowsError(null);
    try {
      const list = await fetchIssuePoRegister({ limit: 1000, sort: "desc" });
      setPoRows(list);
    } catch (e) {
      setPoRows([]);
      setPoRowsError(e instanceof Error ? e.message : String(e));
    } finally {
      setPoRowsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canUsePo) return;
    void loadPoRows();
  }, [canUsePo, loadPoRows]);

  const loadPoAnalytics = useCallback(async () => {
    setPoAnalyticsLoading(true);
    setPoAnalyticsError(null);
    try {
      const data = await fetchIssuePoAnalytics();
      setPoAnalyticsData(data);
    } catch (e) {
      setPoAnalyticsData(null);
      setPoAnalyticsError(e instanceof Error ? e.message : String(e));
    } finally {
      setPoAnalyticsLoading(false);
    }
  }, []);

  const loadIssueAnalytics = useCallback(async () => {
    setIssueAnalyticsLoading(true);
    setIssueAnalyticsError(null);
    try {
      const data = await fetchIssueMaterialAnalytics();
      setIssueAnalyticsData(data);
    } catch (e) {
      setIssueAnalyticsData(null);
      setIssueAnalyticsError(e instanceof Error ? e.message : String(e));
    } finally {
      setIssueAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPoSection || poViewMode !== "register" || !canUsePo) return;
    void loadPoAnalytics();
  }, [isPoSection, poViewMode, canUsePo, loadPoAnalytics]);

  useEffect(() => {
    if (!isIssueSection || issueViewMode !== "register") return;
    void loadIssueAnalytics();
    if (canUsePo) void loadPoAnalytics();
  }, [isIssueSection, issueViewMode, canUsePo, loadIssueAnalytics, loadPoAnalytics]);

  useEffect(() => {
    if (!selectedPoId) {
      setSelectedPoItems([]);
      return;
    }
    let cancelled = false;
    setPoItemsLoading(true);
    void fetchIssuePoItems(selectedPoId)
      .then((items) => {
        if (!cancelled) setSelectedPoItems(items);
      })
      .catch((e) => {
        if (!cancelled) showToast(e instanceof Error ? e.message : "Could not load PO items.", "error");
      })
      .finally(() => {
        if (!cancelled) setPoItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPoId, showToast]);

  useEffect(() => {
    const active =
      isIssueSection && issueViewMode === "generate" && issueGenerateSubMode === "fromPo" && issueFromPoId.trim();
    if (!active) {
      setIssueFromPoItems([]);
      return;
    }
    let cancelled = false;
    const id = issueFromPoId.trim();
    setIssueFromPoItemsLoading(true);
    void fetchIssuePoItems(id)
      .then((items) => {
        if (!cancelled) setIssueFromPoItems(items);
      })
      .catch((e) => {
        if (!cancelled) {
          setIssueFromPoItems([]);
          showToast(e instanceof Error ? e.message : "Could not load PO lines.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setIssueFromPoItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isIssueSection, issueViewMode, issueGenerateSubMode, issueFromPoId, showToast]);

  const issueFromPoSelectOptions = useMemo(() => {
    const openSet = new Set(openPoIdsForIssue);
    const eligible = poRows.filter((p) => openSet.has(p.id));
    return [
      {
        value: "",
        label: poRowsLoading || openPoIdsForIssueLoading
          ? "Loading POs…"
          : eligible.length
            ? "Select PO…"
            : "No POs with pending issue balance",
      },
      ...eligible.map((p) => ({
        value: p.id,
        label: `${p.poNumber} · ${p.milestoneTitle} · ${p.subcontractorFirmName}`,
      })),
    ];
  }, [poRows, poRowsLoading, openPoIdsForIssue, openPoIdsForIssueLoading]);
  const issueFromPoHeader = useMemo(
    () => (issueFromPoId.trim() ? poRows.find((p) => p.id === issueFromPoId.trim()) : undefined),
    [poRows, issueFromPoId],
  );

  const issueFromPoHintMessage = useMemo(() => {
    if (issueFromPoGenerateState.showMessage) return issueFromPoGenerateState.message;
    return "";
  }, [issueFromPoGenerateState]);
  const handleDownloadPoPdfRow = useCallback(
    async (header: IssuePoHeader) => {
      setPoPdfDownloadingId(header.id);
      try {
        const items = await fetchIssuePoItems(header.id);
        if (!items.length) {
          showToast("This PO has no lines to export.", "error");
          return;
        }
        await downloadIssuePoPdf(header, items, { applications });
        showToast(`PDF downloaded (${header.poNumber}).`, "success");
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not download PDF.", "error");
      } finally {
        setPoPdfDownloadingId("");
      }
    },
    [applications, showToast],
  );

  const handleCancelPoRow = useCallback(
    async (header: IssuePoHeader) => {
      const ok = window.confirm(
        `Cancel PO ${header.poNumber}? It will be marked inactive and removed from PO lists. Any issue-material lines created with this PO (not yet issued) will also be removed.`,
      );
      if (!ok) return;
      setPoCancellingId(header.id);
      try {
        const { removedIssueMaterialRows } = await cancelIssuePo(header.id);
        if (selectedPoId === header.id) setSelectedPoId("");
        await Promise.all([loadPoRows(), loadRows(), loadPoAnalytics(), loadIssueAnalytics()]);
        const extra =
          removedIssueMaterialRows > 0
            ? ` · ${removedIssueMaterialRows} issue material row(s) removed`
            : "";
        showToast(`PO ${header.poNumber} cancelled${extra}.`, "success");
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not cancel PO.", "error");
      } finally {
        setPoCancellingId("");
      }
    },
    [loadPoRows, loadRows, loadPoAnalytics, loadIssueAnalytics, selectedPoId, showToast],
  );

  async function handleGenerate() {
    if (!applicationId.trim()) {
      showToast("Select beneficiary.", "error");
      return;
    }
    if (!milestoneTemplateId.trim()) {
      showToast("Select milestone.", "error");
      return;
    }
    if (!selectedMilestone?.linkedResourceIds.length) {
      showToast("Selected milestone has no linked standard resources.", "error");
      return;
    }
    if (selectionMode === "selected" && selectedResourceIds.length === 0) {
      showToast("Select at least one standard resource.", "error");
      return;
    }
    if (previewItems.length === 0) {
      showToast("No valid resource items found for selection.", "error");
      return;
    }
    if (hasMissingCatalog) {
      showToast("Some resource items are not linked to Item Master. Fix in Standard Resources master.", "error");
      return;
    }
    if (!manualIssueGenerateState.canGenerate) {
      showToast(manualIssueGenerateState.message || "Cannot generate issue rows for this selection.", "error");
      return;
    }
    setGenerating(true);
    try {
      const result = await generateIssueMaterialRows({
        applicationId: applicationId.trim(),
        milestoneTemplateId: milestoneTemplateId.trim(),
        mode: selectionMode,
        selectedResourceIds: selectionMode === "selected" ? selectedResourceIds : undefined,
        notes: notes.trim() || undefined,
      });
      showToast(`Issue rows updated. Created: ${result.created}, skipped existing: ${result.skipped}`, "success");
      setNotes("");
      await Promise.all([loadRows(), loadIssueAnalytics(), loadAllIssueRows()]);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not generate issue rows.", "error");
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerateIssueFromPo() {
    if (!issueFromPoId.trim()) {
      showToast("Select a PO.", "error");
      return;
    }
    if (issueFromPoItemsLoading) {
      showToast("Still loading PO lines…", "error");
      return;
    }
    if (issueFromPoItems.length === 0) {
      showToast("This PO has no lines to issue.", "error");
      return;
    }
    if (!issueFromPoGenerateState.canGenerate) {
      showToast(issueFromPoGenerateState.message || "Cannot create issue rows from this PO.", "error");
      return;
    }
    setGeneratingFromPo(true);
    try {
      const quantityOverrides: Record<string, number> = {};
      if (issueFromPoMode === "partial") {
        for (const it of issueFromPoItems) {
          const bal = issueFromPoBalanceByItemId.get(it.id);
          const yetTo = bal?.yetToIssue ?? 0;
          if (!(yetTo > 0)) continue;
          const raw = issueFromPoQtyOverrides[it.id];
          if (raw == null || String(raw).trim() === "") continue;
          const qty = parseQuantityInput(raw);
          if (qty > 0) quantityOverrides[it.id] = qty;
        }
      }
      const result = await generateIssueMaterialFromPo({
        poId: issueFromPoId.trim(),
        notes: issueFromPoNotes.trim() || undefined,
        mode: issueFromPoMode,
        quantityOverrides: issueFromPoMode === "partial" ? quantityOverrides : undefined,
      });
      showToast(
        `PO ${result.poNumber}: ${result.created} issue row(s) created · ${result.beneficiaryCount} beneficiary(ies). Use Inventory outward to dispatch.`,
        "success",
      );
      setIssueFromPoNotes("");
      const balanceData = await fetchPoIssueBalance(issueFromPoId.trim());
      setIssueFromPoBalance(balanceData.items);
      await Promise.all([loadRows(), loadIssueAnalytics(), loadAllIssueRows(), loadOpenPoIdsForIssue()]);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not create issue rows from PO.", "error");
    } finally {
      setGeneratingFromPo(false);
    }
  }

  function buildPoQtyOverrides(): Record<string, number> | null {
    const qtyOverrides: Record<string, number> = {};
    for (const it of poPreviewItems) {
      const baseQty = poLineBaseQtyForRemaining({
        masterQty: it.qty,
        itemId: it.itemId,
        remainingByItem: poRemainingQtyByItemId,
        remainingLoading: poRemainingQtyLoading,
      });
      const raw = poQtyOverrides[it.itemId];
      const parsed = raw != null && String(raw).trim() !== "" ? parseQuantityInput(raw) : baseQty;
      if (!(parsed > 0)) {
        showToast(`Quantity must be greater than zero for item: ${it.itemLabel}`, "error");
        return null;
      }
      if (Math.abs(parsed - it.qty) > 0.0000001) qtyOverrides[it.itemId] = parsed;
    }
    return qtyOverrides;
  }

  function openPoShipToModal() {
    if (!poApplicationIds.length) {
      showToast("Select at least one beneficiary.", "error");
      return;
    }
    if (!poMilestoneTemplateId.trim()) {
      showToast("Select milestone.", "error");
      return;
    }
    if (!poSubcontractorValidation.ok) {
      showToast("Selected beneficiaries must belong to the same subcontractor.", "error");
      return;
    }
    if (poSelectionMode === "partial" && poSelectedResourceIds.length === 0 && poSelectedResourceItemIds.length === 0) {
      showToast("For partial PO, select resources and/or specific items.", "error");
      return;
    }
    if (!poPreviewItems.length) {
      showToast("No valid items selected for PO.", "error");
      return;
    }
    if (poHasMissingCatalog) {
      showToast("Some selected items are not linked to Item Master.", "error");
      return;
    }
    if (buildPoQtyOverrides() === null) return;
    setPoShipToModalOpen(true);
  }

  async function runGeneratePoWithShipTo(shipTo: PoShipToChoice) {
    const qtyOverrides = buildPoQtyOverrides();
    if (qtyOverrides === null) return;
    setPoShipToModalOpen(false);
    setPoGenerating(true);
    try {
      const shipToApi: IssuePoShipToInput = { kind: "store", storeYardId: shipTo.storeYardId };
      const result = await generateIssuePo({
        milestoneTemplateId: poMilestoneTemplateId.trim(),
        applicationIds: poApplicationIds,
        selectionMode: poSelectionMode,
        selectedResourceIds: poSelectionMode === "partial" ? poSelectedResourceIds : undefined,
        selectedResourceItemIds: poSelectionMode === "partial" ? poSelectedResourceItemIds : undefined,
        quantityOverrides: Object.keys(qtyOverrides).length ? qtyOverrides : undefined,
        shipTo: shipToApi,
      });
      setPoGenerateSuccess({
        po: result.po,
        items: result.items,
      });
      setSelectedPoId(result.po.id);
      setPoQtyOverrides({});
      await Promise.all([loadPoRows(), loadRows(), loadPoAnalytics(), loadIssueAnalytics()]);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not generate PO.", "error");
    } finally {
      setPoGenerating(false);
    }
  }

  return (
    <div className="issue-material-register-page">
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: isPoSection ? "PO Register" : "Material Register" },
        ]}
        title={isPoSection ? "PO Register" : "Material Register"}
        actions={
          isPoSection && canUsePo ? (
            <Link className="btn btn--tg-secondary btn--sm" to="/inventory/po-format">
              PO format
            </Link>
          ) : undefined
        }
      />

      <PoGenerateShipToModal
        open={poShipToModalOpen}
        storeYards={storeYards}
        onCancel={() => setPoShipToModalOpen(false)}
        onConfirm={(choice) => void runGeneratePoWithShipTo(choice)}
      />

      {poGenerateSuccess ? (
        <PoGenerateSuccessTicket
          po={poGenerateSuccess.po}
          items={poGenerateSuccess.items}
          applications={applications}
          onClose={() => setPoGenerateSuccess(null)}
          onCancel={async () => {
            const { removedIssueMaterialRows } = await cancelIssuePo(poGenerateSuccess.po.id);
            setPoGenerateSuccess(null);
            setSelectedPoId("");
            await Promise.all([loadPoRows(), loadRows(), loadPoAnalytics(), loadIssueAnalytics()]);
            const extra =
              removedIssueMaterialRows > 0
                ? ` · ${removedIssueMaterialRows} issue material row(s) removed`
                : "";
            showToast(`PO ${poGenerateSuccess.po.poNumber} cancelled${extra}.`, "success");
          }}
        />
      ) : null}

      {isPoSection && canUsePo ? (
        <div className="issue-mode-toggle-wrap">
          <div className="issue-mode-toggle" role="tablist" aria-label="Issue material mode">
            <button
              type="button"
              role="tab"
              aria-selected={poViewMode === "generate"}
              className={`issue-mode-toggle__btn ${poViewMode === "generate" ? "is-active" : ""}`}
              onClick={() => setPoViewMode("generate")}
            >
              Generate PO
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={poViewMode === "register"}
              className={`issue-mode-toggle__btn ${poViewMode === "register" ? "is-active" : ""}`}
              onClick={() => setPoViewMode("register")}
            >
              PO Register
            </button>
          </div>
        </div>
      ) : null}
      {isIssueSection ? (
        <div className="issue-mode-toggle-wrap">
          <div className="issue-mode-toggle" role="tablist" aria-label="Issue material mode">
            <button
              type="button"
              role="tab"
              aria-selected={issueViewMode === "generate"}
              className={`issue-mode-toggle__btn ${issueViewMode === "generate" ? "is-active" : ""}`}
              onClick={() => setIssueViewMode("generate")}
            >
              Issue Material
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={issueViewMode === "register"}
              className={`issue-mode-toggle__btn ${issueViewMode === "register" ? "is-active" : ""}`}
              onClick={() => setIssueViewMode("register")}
            >
              Register
            </button>
          </div>
        </div>
      ) : null}

      {isPoSection && canUsePo && poViewMode === "generate" ? (
        <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
          <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
            Generate beneficiary-wise PO for selected milestone resources. Single or multiple beneficiaries are supported only when they
            belong to the same subcontractor. Generated PO auto-creates Issue Material rows.
          </p>
          <h3 className="form-section-title">Generate PO</h3>
          <p className="text-muted" style={{ marginTop: "-0.35rem", marginBottom: "0.75rem", fontSize: "0.88rem" }}>
            Step 1: Choose beneficiaries {"->"} Step 2: Choose milestone and full/partial mode {"->"} Step 3:
            Review/edit quantities below, then generate PO.
          </p>
          <p className="text-muted" style={{ marginTop: "-0.5rem", marginBottom: "0.75rem", fontSize: "0.82rem" }}>
            <strong>Limits (per beneficiary, per milestone):</strong> one <em>full</em> PO locks the milestone — no more POs.
            After any PO exists, use <em>partial</em> only. Each line cannot exceed the remaining quantity (master plan minus
            quantities already on earlier POs for that item).
          </p>
          <div className="issue-po-flow">
            <section className="issue-po-flow__section">
              <span className="inv-outward-issue__label">Step 1 — Beneficiaries (single or multiple)</span>
              <p className="text-muted" style={{ margin: "0 0 0.5rem", fontSize: "0.84rem" }}>
                After you pick beneficiaries here, Step 2 only lists milestones where those beneficiaries can still get a PO.
                Phases where someone already has a <em>full</em> PO are omitted from the milestone dropdown.
              </p>
              <div className="goods-receipts-filters__grid" style={{ marginBottom: "0.5rem" }}>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Search</span>
                  <input
                    type="search"
                    className="form-control"
                    value={poBeneficiarySearch}
                    onChange={(e) => setPoBeneficiarySearch(e.target.value)}
                    placeholder="Beneficiary name / application id"
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">District</span>
                  <SearchableSelect
                    id="po-beneficiary-district"
                    value={poDistrictFilter}
                    onChange={setPoDistrictFilter}
                    options={poDistrictSelectOptions}
                    placeholder="All districts"
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Mandal</span>
                  <SearchableSelect
                    id="po-beneficiary-mandal"
                    value={poMandalFilter}
                    onChange={setPoMandalFilter}
                    options={poMandalSelectOptions}
                    placeholder="All mandals"
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Village</span>
                  <SearchableSelect
                    id="po-beneficiary-village"
                    value={poVillageFilter}
                    onChange={setPoVillageFilter}
                    options={poVillageSelectOptions}
                    placeholder="All villages"
                  />
                </label>
                {poZoneGroupSelectOptions.length > 1 && (
                  <label className="goods-receipts-filters__field">
                    <span className="goods-receipts-filters__label">Group</span>
                    <SearchableSelect
                      id="po-beneficiary-zone-group"
                      value={poZoneGroupFilter}
                      onChange={setPoZoneGroupFilter}
                      options={poZoneGroupSelectOptions}
                      placeholder="All groups"
                    />
                  </label>
                )}
              </div>
              <p className="text-muted" style={{ margin: "0 0 0.4rem", fontSize: "0.85rem" }}>
                Showing {visiblePoBeneficiaries.length} of {eligibleApplications.length} eligible beneficiaries
                {notInitiatedApplications.length > 0 && (
                  <span style={{ color: "#d97706", marginLeft: "0.5rem", fontWeight: 500 }}>
                    · {notInitiatedApplications.length} blocked (not initiated)
                  </span>
                )}
              </p>
              <div className="toolbar-row" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                <button
                  type="button"
                  className="btn btn--sm btn--tg-secondary"
                  onClick={() =>
                    setPoApplicationIds((prev) => [...new Set([...prev, ...visiblePoBeneficiaryIds])])
                  }
                  disabled={visiblePoBeneficiaries.length === 0}
                >
                  {poVillageFilter !== "all" ? "Select village beneficiaries" : "Select all visible"}
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--tg-secondary"
                  onClick={() =>
                    setPoApplicationIds((prev) => prev.filter((id) => !visiblePoBeneficiaries.some((a) => a.id === id)))
                  }
                  disabled={visiblePoBeneficiaries.length === 0}
                >
                  Clear visible
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--tg-secondary"
                  onClick={() => setPoApplicationIds([])}
                  disabled={poApplicationIds.length === 0}
                >
                  Clear all selected
                </button>
                <label className="text-muted" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", marginLeft: "0.25rem" }}>
                  <input
                    type="checkbox"
                    checked={poShowSelectedOnly}
                    onChange={(e) => setPoShowSelectedOnly(e.target.checked)}
                  />
                  Show only selected
                </label>
              </div>
              <p className="text-muted" style={{ margin: "0 0 0.5rem", fontSize: "0.84rem" }}>
                Selected: <strong>{poApplicationIds.length}</strong>
                {poSelectedBeneficiaryNames.length ? ` (${poSelectedBeneficiaryNames.slice(0, 3).join(", ")}${poSelectedBeneficiaryNames.length > 3 ? "..." : ""})` : ""}
              </p>
              <div className="issue-po-beneficiary-table-wrap" role="group" aria-label="Select beneficiaries">
                <table className="data-table issue-po-beneficiary-table">
                  <thead>
                    <tr>
                      <th style={{ width: "2.5rem" }}>
                        <input
                          type="checkbox"
                          aria-label="Select all visible beneficiaries"
                          checked={poAllVisibleSelected}
                          onChange={(e) =>
                            e.target.checked
                              ? setPoApplicationIds((prev) => [...new Set([...prev, ...visiblePoBeneficiaryIds])])
                              : setPoApplicationIds((prev) => prev.filter((id) => !visiblePoBeneficiaryIds.includes(id)))
                          }
                        />
                      </th>
                      <th>Application ID</th>
                      <th>Beneficiary name</th>
                      <th>District</th>
                      <th>Mandal</th>
                      <th>Village</th>
                      <th>Subcontractor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePoBeneficiaries.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={poApplicationIds.includes(a.id)}
                            onChange={() =>
                              setPoApplicationIds((prev) =>
                                prev.includes(a.id) ? prev.filter((id) => id !== a.id) : [...prev, a.id],
                              )
                            }
                          />
                        </td>
                        <td className="mono">{a.mobile.applicationId}</td>
                        <td>{a.mobile.beneficiaryName}</td>
                        <td>{a.district}</td>
                        <td>{a.mandal}</td>
                        <td>{a.village}</td>
                        <td>{subcontractorNameById.get(a.subcontractor.subcontractorMasterId || "") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {visiblePoBeneficiaries.length === 0 ? (
                <p className="text-muted" style={{ marginTop: "0.45rem" }}>
                  No beneficiaries match these filters.
                </p>
              ) : null}

              {/* ── Not-initiated beneficiaries warning ────────────────── */}
              {notInitiatedApplications.length > 0 && (
                <div className="po-not-initiated-banner">
                  <div className="po-not-initiated-banner__header">
                    <span className="po-not-initiated-banner__icon">⚠️</span>
                    <strong>
                      {notInitiatedApplications.length} beneficiar{notInitiatedApplications.length !== 1 ? "ies" : "y"} blocked — Project Initiation required
                    </strong>
                    <a href="/project-initiation" className="po-not-initiated-banner__link">
                      Go to Project Initiation →
                    </a>
                  </div>
                  <p className="po-not-initiated-banner__desc">
                    These beneficiaries have a subcontractor assigned but no planned start date set.
                    Set a start date in <strong>Planning → Project Initiation</strong> before generating their PO.
                  </p>
                  <div className="po-not-initiated-banner__list">
                    {notInitiatedApplications.map((a) => (
                      <span key={a.id} className="po-not-initiated-banner__chip">
                        {a.mobile.beneficiaryName || a.mobile.applicationId}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="issue-po-flow__section">
              <span className="inv-outward-issue__label">Step 2 — Milestone and PO setup</span>
              <div className="inv-outward-issue__grid">
                <div className="inv-outward-issue__field inv-outward-issue__field--full issue-po-milestone-stack">
                  <span className="inv-outward-issue__label">Milestone</span>
                  <SearchableSelect
                    id="po-milestone-template"
                    value={poMilestoneTemplateId}
                    onChange={setPoMilestoneTemplateId}
                    options={poMilestoneSelectOptions}
                    placeholder={
                      poApplicationIds.length > 0 &&
                      !poBlockedMilestonesLoading &&
                      milestoneTemplates.length > 0 &&
                      poMilestoneSelectOptions.length <= 1
                        ? "No milestones left for selection"
                        : "Select…"
                    }
                  />
                  {poApplicationIds.length > 0 && poBlockedMilestonesLoading ? (
                    <p className="inv-outward-issue__hint issue-po-milestone-stack__hint">
                      Checking which phases still allow a PO for the selected beneficiaries…
                    </p>
                  ) : null}
                  {poApplicationIds.length > 0 && !poBlockedMilestonesLoading && poBlockedFullPos.length > 0 ? (
                    <div
                      className="issue-po-blocked-summary"
                      role="region"
                      aria-label="Milestone phases already covered by a full purchase order"
                    >
                      <p className="issue-po-blocked-summary__lead">
                        These phases are hidden above — materials are already covered by a{" "}
                        <strong>full</strong> PO for the listed beneficiary. PO number(s):
                      </p>
                      <ul className="issue-po-blocked-summary__milestones">
                        {poBlockedFullPos.map((block) => (
                          <li key={block.milestoneTemplateId} className="issue-po-blocked-summary__milestone">
                            <div className="issue-po-blocked-summary__milestone-title">{block.milestoneTitle}</div>
                            <ul className="issue-po-blocked-summary__lines">
                              {block.lines.map((line) => {
                                const app = appById.get(line.applicationId);
                                const name = (app?.mobile.beneficiaryName ?? "").trim() || "Beneficiary";
                                const appNo = (app?.mobile.applicationId ?? "").trim() || line.applicationId;
                                const pos = line.poNumbers.filter(Boolean);
                                return (
                                  <li key={`${block.milestoneTemplateId}-${line.applicationId}`}>
                                    <span className="issue-po-blocked-summary__name">{name}</span>
                                    <span className="issue-po-blocked-summary__app mono">{appNo}</span>
                                    <span className="issue-po-blocked-summary__po mono">{pos.length ? pos.join(", ") : "—"}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">PO mode</span>
                  <SearchableSelect
                    id="po-selection-mode"
                    value={poSelectionMode}
                    onChange={(v) => setPoSelectionMode(v === "partial" ? "partial" : "full")}
                    options={poModeSelectOptions}
                    placeholder="Select mode…"
                  />
                </label>
                {poSelectionMode === "partial" ? (
                  <>
                    <div className="inv-outward-issue__field inv-outward-issue__field--full">
                      <span className="inv-outward-issue__label">Step 3 — Select resources</span>
                      <div className="milestone-timeline-check-grid" role="group" aria-label="Select resources for PO">
                        {poLinkedResources.map((r) => (
                          <label key={r.id} className="milestone-timeline-check">
                            <input
                              type="checkbox"
                              checked={poSelectedResourceIds.includes(r.id)}
                              onChange={() =>
                                setPoSelectedResourceIds((prev) =>
                                  prev.includes(r.id) ? prev.filter((id) => id !== r.id) : [...prev, r.id],
                                )
                              }
                            />
                            <span className="milestone-timeline-check__text milestone-timeline-check__text--single">
                              <span className="milestone-timeline-check__title">{r.title}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="inv-outward-issue__field inv-outward-issue__field--full">
                      <span className="inv-outward-issue__label">Step 3 — Select specific items (optional)</span>
                      <div className="milestone-timeline-check-grid" role="group" aria-label="Select resource items for PO">
                        {poLinkedResources
                          .filter((r) => poSelectedResourceIds.includes(r.id))
                          .flatMap((r) =>
                            r.items.map((item) => (
                              <label key={item.id} className="milestone-timeline-check">
                                <input
                                  type="checkbox"
                                  checked={poSelectedResourceItemIds.includes(item.id)}
                                  onChange={() =>
                                    setPoSelectedResourceItemIds((prev) =>
                                      prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
                                    )
                                  }
                                />
                                <span className="milestone-timeline-check__text">
                                  <span className="milestone-timeline-check__title">{item.label}</span>
                                  <span className="milestone-timeline-check__meta">
                                    {item.quantity} {item.unit}
                                  </span>
                                </span>
                              </label>
                            )),
                          )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            <section className="issue-po-flow__section">
              <span className="inv-outward-issue__label">Step 3 — PO preview items</span>
              {poApplicationIds.length > 0 &&
              poMilestoneTemplateId.trim() &&
              !poRemainingQtyLoading &&
              Object.keys(poRemainingQtyByItemId).length > 0 ? (
                <p className="inv-outward-issue__hint" style={{ marginTop: "0.35rem", marginBottom: "0.65rem" }}>
                  Quantities default to what you can still PO for the selected beneficiaries (master plan minus quantities
                  already on earlier POs for this milestone).
                </p>
              ) : null}
              {poPreviewWithQty.length === 0 ? (
                <p className="text-muted">Select beneficiaries/milestone and mode to preview PO rows.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Resource</th>
                        <th>Item</th>
                        <th>Item master code</th>
                        <th>HSN/SAC</th>
                        <th title="Quantity from the milestone / standard resource master plan (before earlier POs).">
                          Planned qty
                        </th>
                        <th title="Defaults to remaining qty after earlier POs for this milestone; edit if you need less on this PO.">
                          Qty (editable)
                        </th>
                        <th>Unit</th>
                        <th>Rate (₹)</th>
                        <th>Line (₹)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poPreviewWithQty.map((it) => {
                        const rem = poRemainingQtyByItemId[it.itemId];
                        const plannedQty = it.qty;
                        const qtyLineNoRemaining =
                          !poRemainingQtyLoading && rem !== undefined && rem <= 0;
                        const qtyAlreadyOnEarlierPos =
                          !poRemainingQtyLoading && rem !== undefined
                            ? Math.max(0, Math.round((plannedQty - rem) * 10000) / 10000)
                            : 0;
                        const partialEarlierPo =
                          !qtyLineNoRemaining &&
                          rem !== undefined &&
                          rem > 0 &&
                          qtyAlreadyOnEarlierPos > 0.0000001;
                        const qtyHint =
                          !poRemainingQtyLoading && rem !== undefined
                            ? `Master planned ${formatQtyInputDisplay(plannedQty)} ${it.unit} · Remaining you can PO now: ${formatQtyInputDisplay(rem)} ${it.unit}`
                            : undefined;
                        return (
                        <tr
                          key={`${it.resourceId}-${it.itemId}`}
                          className={qtyLineNoRemaining ? "issue-po-preview-row--qty-exhausted" : undefined}
                        >
                          <td>{it.resourceTitle}</td>
                          <td>{it.itemLabel}</td>
                          <td className="mono">{it.catalogStandardItemId || "— missing mapping —"}</td>
                          <td className="mono">{it.hsnCode?.trim() || "—"}</td>
                          <td className="mono">{formatQtyInputDisplay(plannedQty)}</td>
                          <td>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control"
                              style={{ maxWidth: "7rem" }}
                              value={it.qtyInputValue}
                              disabled={qtyLineNoRemaining}
                              title={qtyHint}
                              onChange={(e) =>
                                setPoQtyOverrides((prev) => ({
                                  ...prev,
                                  [it.itemId]: sanitizeQtyFieldInput(e.target.value),
                                }))
                              }
                            />
                          </td>
                          <td>{it.unit}</td>
                          <td className="mono">{fmtInrAmount(it.rate ?? 0)}</td>
                          <td className="mono">{fmtInrAmount(it.lineAmount)}</td>
                          <td className="issue-po-preview-status">
                            {qtyLineNoRemaining ? (
                              <span className="issue-po-preview-status__done">Already PO generated</span>
                            ) : partialEarlierPo ? (
                              <span className="issue-po-preview-status__partial">
                                {formatQtyInputDisplay(qtyAlreadyOnEarlierPos)} qty PO generated
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {poApplicationIds.length > 1 && !poSubcontractorValidation.ok ? (
              <p className="inv-outward-issue__hint">Selected beneficiaries must have the same subcontractor.</p>
            ) : null}
            <div className="inv-outward-issue__actions">
              <button
                type="button"
                className="btn btn--primary btn--tg btn--lg"
                disabled={poGenerating || !poCanGenerate.ok}
                onClick={() => openPoShipToModal()}
              >
                {poGenerating ? "Generating…" : "Generate PO"}
              </button>
              {!poCanGenerate.ok ? <span className="text-muted">{poCanGenerate.reason}</span> : null}
            </div>
          </div>
        </div>
      ) : null}

      {isPoSection && canUsePo && poViewMode === "register" ? (
        <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
          <div className="po-analytics-header">
            <h3 className="form-section-title" style={{ marginTop: 0, marginBottom: 0 }}>
              PO analytics
            </h3>
            <button
              type="button"
              className="btn btn--sm btn--tg-secondary"
              onClick={() => void loadPoAnalytics()}
              disabled={poAnalyticsLoading}
            >
              {poAnalyticsLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          {poAnalyticsError ? (
            <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
              {poAnalyticsError}
            </p>
          ) : null}

          <div className="kpi-grid kpi-grid--cols-4" style={{ marginBottom: "1rem" }}>
            <div className="kpi-card kpi-card--pastel">
              <div className="kpi-card__label">Total POs generated</div>
              <div className="kpi-card__value">{poAnalyticsData?.totals.totalPo ?? "—"}</div>
            </div>
            <div className="kpi-card kpi-card--pastel">
              <div className="kpi-card__label">Total beneficiaries</div>
              <div className="kpi-card__value">
                {poAnalyticsData != null ? poAnalyticsData.totals.totalEligibleBeneficiaries : "—"}
              </div>
            </div>
            <div className="kpi-card kpi-card--pastel">
              <div className="kpi-card__label">Beneficiaries — complete PO</div>
              <div className="kpi-card__value">{poAnalyticsData?.totals.beneficiariesWithFullPo ?? "—"}</div>
            </div>
            <div className="kpi-card kpi-card--pastel">
              <div className="kpi-card__label">Beneficiaries — partial PO</div>
              <div className="kpi-card__value">{poAnalyticsData?.totals.beneficiariesWithPartialPo ?? "—"}</div>
            </div>
          </div>

          <h3 className="form-section-title">PO breakdown by milestone</h3>
          {poAnalyticsLoading && !poAnalyticsData ? (
            <p className="text-muted">Loading…</p>
          ) : !poAnalyticsData || poAnalyticsData.byMilestone.length === 0 ? (
            <p className="text-muted">No data.</p>
          ) : (
            <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Milestone</th>
                    <th>POs</th>
                    <th>Full</th>
                    <th>Partial</th>
                    <th>Beneficiaries</th>
                    <th>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {poAnalyticsData.byMilestone.map((m) => (
                    <tr key={m.milestoneTemplateId || m.milestoneTitle}>
                      <td>{m.milestoneTitle}</td>
                      <td>{m.poCount}</td>
                      <td>{m.fullCount}</td>
                      <td>{m.partialCount}</td>
                      <td>{m.uniqueBeneficiaries}</td>
                      <td>{m.itemCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="form-section-title">PO breakdown by subcontractor</h3>
          {poAnalyticsLoading && !poAnalyticsData ? (
            <p className="text-muted">Loading…</p>
          ) : !poAnalyticsData || poAnalyticsData.bySubcontractor.length === 0 ? (
            <p className="text-muted">No data.</p>
          ) : (
            <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subcontractor</th>
                    <th>POs</th>
                    <th>Full</th>
                    <th>Partial</th>
                    <th>Beneficiaries</th>
                    <th>Items</th>
                  </tr>
                </thead>
                <tbody>
                  {poAnalyticsData.bySubcontractor.map((s) => (
                    <tr key={s.subcontractorMasterId || s.subcontractorFirmName}>
                      <td>{s.subcontractorFirmName}</td>
                      <td>{s.poCount}</td>
                      <td>{s.fullCount}</td>
                      <td>{s.partialCount}</td>
                      <td>{s.uniqueBeneficiaries}</td>
                      <td>{s.itemCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Generated PO register
          </h3>
          {poRowsLoading ? (
            <p className="text-muted">Loading…</p>
          ) : poRowsError ? (
            <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
              {poRowsError}
            </p>
          ) : poRows.length === 0 ? (
            <p className="text-muted">No PO records yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>PO number</th>
                    <th>Subcontractor</th>
                    <th>Milestone</th>
                    <th>Beneficiaries</th>
                    <th>Mode</th>
                    <th>When</th>
                    <th style={{ width: "14rem" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {poRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedPoId((prev) => (prev === row.id ? "" : row.id))}
                      style={{
                        cursor: "pointer",
                        background: selectedPoId === row.id ? "var(--dash-row-selected, #eff6ff)" : undefined,
                      }}
                    >
                      <td className="mono">{row.poNumber}</td>
                      <td>{row.subcontractorFirmName}</td>
                      <td>{row.milestoneTitle}</td>
                      <td>{row.beneficiaryCount}</td>
                      <td style={{ textTransform: "capitalize" }}>{row.selectionMode}</td>
                      <td className="text-muted">{formatWhen(row.createdAt)}</td>
                      <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                        <button
                          type="button"
                          className="btn btn--tg-secondary btn--sm"
                          disabled={poPdfDownloadingId === row.id || poCancellingId === row.id}
                          aria-label={`Download PDF for ${row.poNumber}`}
                          onClick={() => void handleDownloadPoPdfRow(row)}
                        >
                          {poPdfDownloadingId === row.id ? "PDF…" : "Download PDF"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          style={{ marginLeft: "0.35rem" }}
                          disabled={poPdfDownloadingId === row.id || poCancellingId === row.id}
                          aria-label={`Cancel ${row.poNumber}`}
                          onClick={() => void handleCancelPoRow(row)}
                        >
                          {poCancellingId === row.id ? "Cancelling…" : "Cancel"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {selectedPoId ? (
            <>
              <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
                PO items
              </h3>
              {poItemsLoading ? (
                <p className="text-muted">Loading PO items…</p>
              ) : selectedPoItems.length === 0 ? (
                <p className="text-muted">No items for selected PO.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Beneficiary</th>
                        <th>Resource item</th>
                        <th>Mapped item</th>
                        <th>HSN/SAC</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Rate (₹)</th>
                        <th>Line (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPoItems.map((it) => (
                        <tr key={it.id}>
                          <td>
                            <div className="table-name">{it.beneficiaryName ?? "—"}</div>
                            <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                              {it.beneficiaryMobileApplicationId ?? it.applicationId}
                            </div>
                          </td>
                          <td>
                            <div>{it.resourceItemLabel}</div>
                            <div className="text-muted" style={{ fontSize: "0.85em" }}>
                              {it.standardResourceTitle}
                            </div>
                          </td>
                          <td>
                            <div>{it.catalogStandardItemId || "—"}</div>
                            <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                              {it.catalogStandardItemId}
                            </div>
                          </td>
                          <td className="mono">{it.hsnCode?.trim() || "—"}</td>
                          <td>{it.plannedQty}</td>
                          <td>{it.resourceItemUnit}</td>
                          <td className="mono">{fmtInrAmount(it.resourceItemRate ?? 0)}</td>
                          <td className="mono">
                            {fmtInrAmount(Math.round(it.plannedQty * (it.resourceItemRate ?? 0) * 100) / 100)}
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      {isIssueSection && issueViewMode === "generate" ? (
      <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
        <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
          Create beneficiary-wise material issue rows. Dispatch stock later in{" "}
          <Link to="/inventory/outward">Inventory outward</Link>.
          {canUsePo
            ? " Choose manual entry (beneficiary and milestone) or create rows directly from an existing PO."
            : " Pick beneficiary and milestone-linked resources below."}
        </p>
        <h3 className="form-section-title">Generate issue rows</h3>
        {canUsePo ? (
          <fieldset className="issue-source-fieldset">
            <legend className="issue-source-fieldset__legend">How to create issue rows</legend>
            <div className="issue-source-radios">
              <label className="issue-source-radio-row">
                <input
                  type="radio"
                  name="issueGenerateSource"
                  checked={issueGenerateSubMode === "manual"}
                  onChange={() => setIssueGenerateSubMode("manual")}
                />
                <span className="issue-source-radio-row__text">
                  <span className="issue-source-radio-row__title">Manual — beneficiary and milestone</span>
                  <span className="issue-source-radio-row__desc">Select one beneficiary, milestone, and resources (current flow).</span>
                </span>
              </label>
              <label className="issue-source-radio-row">
                <input
                  type="radio"
                  name="issueGenerateSource"
                  checked={issueGenerateSubMode === "fromPo"}
                  onChange={() => setIssueGenerateSubMode("fromPo")}
                />
                <span className="issue-source-radio-row__text">
                  <span className="issue-source-radio-row__title">Direct from PO</span>
                  <span className="issue-source-radio-row__desc">Load beneficiaries, items, and quantities from an existing purchase order.</span>
                </span>
              </label>
            </div>
          </fieldset>
        ) : null}
        {issueGenerateSubMode === "fromPo" && canUsePo ? (
          <>
            <p className="text-muted" style={{ marginTop: "-0.35rem", marginBottom: "0.75rem", fontSize: "0.88rem" }}>
              Select a PO with remaining issue balance. Choose full balance or enter partial quantities per line, then create issue rows. Outward dispatch is limited to issued quantity — PO visibility is based on how much has been issued, not outward completion.
            </p>
            <div className="inv-outward-issue__grid">
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Issue mode</span>
                <SearchableSelect
                  id="issue-from-po-mode"
                  value={issueFromPoMode}
                  onChange={(v) => setIssueFromPoMode(v === "partial" ? "partial" : "full")}
                  options={[
                    { value: "full", label: "Full balance (remaining PO qty)" },
                    { value: "partial", label: "Partial qty (per line)" },
                  ]}
                  placeholder="Select mode…"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Purchase order</span>
                <SearchableSelect
                  id="issue-from-po-id"
                  value={issueFromPoId}
                  onChange={setIssueFromPoId}
                  options={issueFromPoSelectOptions}
                  placeholder="Select PO…"
                />
              </label>
              {issueFromPoHeader ? (
                <p className="inv-outward-issue__field inv-outward-issue__field--full text-muted" style={{ margin: 0, fontSize: "0.88rem" }}>
                  <strong className="mono">{issueFromPoHeader.poNumber}</strong>
                  {" · "}
                  {issueFromPoHeader.selectionMode === "full" ? "Full PO" : "Partial PO"}
                  {" · "}
                  {issueFromPoHeader.milestoneTitle}
                  {" · "}
                  {issueFromPoHeader.subcontractorFirmName}
                </p>
              ) : null}
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Notes (optional)</span>
                <textarea
                  className="form-control inv-outward-issue__notes"
                  value={issueFromPoNotes}
                  onChange={(e) => setIssueFromPoNotes(e.target.value)}
                  rows={2}
                  placeholder="Extra note stored on generated issue rows…"
                />
              </label>
            </div>
            <div className="inv-outward-issue__actions">
              {issueFromPoHintMessage ? (
                <p className="inv-outward-issue__hint" style={{ margin: "0 0 0.75rem", flex: "1 1 100%" }}>
                  {issueFromPoHintMessage}
                </p>
              ) : null}
              <button
                type="button"
                className="btn btn--primary btn--tg btn--lg"
                disabled={
                  generatingFromPo ||
                  !issueFromPoId.trim() ||
                  issueFromPoItemsLoading ||
                  issueFromPoBalanceLoading ||
                  issueFromPoItems.length === 0 ||
                  !issueFromPoGenerateState.canGenerate
                }
                onClick={() => void handleGenerateIssueFromPo()}
              >
                {generatingFromPo ? "Creating…" : "Create issue rows from PO"}
              </button>
              <button
                type="button"
                className="btn btn--tg-secondary btn--lg"
                disabled={poRowsLoading || openPoIdsForIssueLoading}
                onClick={() => {
                  void loadPoRows();
                  void loadOpenPoIdsForIssue();
                }}
              >
                {poRowsLoading || openPoIdsForIssueLoading ? "Refreshing…" : "Refresh PO list"}
              </button>
            </div>
            <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
              PO lines (preview)
            </h3>
            {!issueFromPoId.trim() ? (
              <p className="text-muted">Select a PO to see beneficiaries, resources, and quantities.</p>
            ) : issueFromPoItemsLoading || issueFromPoBalanceLoading ? (
              <p className="text-muted">Loading PO lines…</p>
            ) : issueFromPoItems.length === 0 ? (
              <p className="text-muted">No lines on this PO.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Beneficiary</th>
                      <th>Item</th>
                      <th>Item master</th>
                      <th>HSN/SAC</th>
                      <th>PO Qty</th>
                      <th>Already issued</th>
                      <th>Yet to issue</th>
                      <th style={{ width: "8rem" }}>This issue qty</th>
                      <th>Unit</th>
                      <th>Rate (₹)</th>
                      <th>Line (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueFromPoItems.map((it) => {
                      const bal = issueFromPoBalanceByItemId.get(it.id);
                      const poQty = bal?.poQty ?? it.plannedQty;
                      const alreadyIssued = bal?.alreadyIssued ?? 0;
                      const yetToIssue = bal?.yetToIssue ?? poQty;
                      const issueQtyDisplay =
                        issueFromPoMode === "full"
                          ? yetToIssue > 0
                            ? formatQtyInputDisplay(yetToIssue)
                            : "0"
                          : issueFromPoQtyOverrides[it.id] ?? "";
                      const issueQtyNum =
                        issueFromPoMode === "full"
                          ? yetToIssue
                          : parseQuantityInput(issueQtyDisplay);
                      return (
                      <tr key={it.id}>
                        <td>
                          <div className="table-name">{it.beneficiaryName ?? "—"}</div>
                          <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                            {it.beneficiaryMobileApplicationId ?? it.applicationId}
                          </div>
                        </td>
                        <td>
                          <div>{it.resourceItemLabel}</div>
                          <div className="text-muted" style={{ fontSize: "0.85em" }}>
                            {it.standardResourceTitle}
                          </div>
                        </td>
                        <td className="mono">{it.catalogStandardItemId || "—"}</td>
                        <td className="mono">{it.hsnCode?.trim() || "—"}</td>
                        <td>{formatQtyInputDisplay(poQty)}</td>
                        <td>{formatQtyInputDisplay(alreadyIssued)}</td>
                        <td>{formatQtyInputDisplay(yetToIssue)}</td>
                        <td>
                          {yetToIssue > 0 ? (
                            <input
                              className="inv-receive-modal__input inv-receive-modal__qty-input"
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={issueQtyDisplay}
                              disabled={issueFromPoMode === "full"}
                              placeholder={issueFromPoMode === "partial" ? "0" : undefined}
                              onChange={(e) =>
                                setIssueFromPoQtyOverrides((prev) => ({
                                  ...prev,
                                  [it.id]: sanitizeQtyFieldInput(e.target.value),
                                }))
                              }
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{it.resourceItemUnit}</td>
                        <td className="mono">{fmtInrAmount(it.resourceItemRate ?? 0)}</td>
                        <td className="mono">
                          {fmtInrAmount(Math.round(issueQtyNum * (it.resourceItemRate ?? 0) * 100) / 100)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-muted" style={{ marginTop: "-0.35rem", marginBottom: "0.75rem", fontSize: "0.88rem" }}>
              Step 1: Filter and select beneficiary {"->"} Step 2: Select milestone and mode {"->"} Step 3:
              Select resources (if needed) {"->"} Step 4: Generate.
            </p>
            <div className="inv-outward-issue__grid">
          <div className="inv-outward-issue__field inv-outward-issue__field--full">
            <span className="inv-outward-issue__label">Step 1 — Beneficiary filters</span>
            <div className="goods-receipts-filters__grid" style={{ marginBottom: "0.5rem" }}>
              <label className="goods-receipts-filters__field">
                <span className="goods-receipts-filters__label">Search</span>
                <input
                  type="search"
                  className="form-control"
                  value={issueBeneficiarySearch}
                  onChange={(e) => setIssueBeneficiarySearch(e.target.value)}
                  placeholder="Beneficiary name / application id"
                />
              </label>
              <label className="goods-receipts-filters__field">
                <span className="goods-receipts-filters__label">District</span>
                <SearchableSelect
                  id="issue-beneficiary-district"
                  value={issueDistrictFilter}
                  onChange={setIssueDistrictFilter}
                  options={issueDistrictSelectOptions}
                  placeholder="All districts"
                />
              </label>
              <label className="goods-receipts-filters__field">
                <span className="goods-receipts-filters__label">Mandal</span>
                <SearchableSelect
                  id="issue-beneficiary-mandal"
                  value={issueMandalFilter}
                  onChange={setIssueMandalFilter}
                  options={issueMandalSelectOptions}
                  placeholder="All mandals"
                />
              </label>
              <label className="goods-receipts-filters__field">
                <span className="goods-receipts-filters__label">Village</span>
                <SearchableSelect
                  id="issue-beneficiary-village"
                  value={issueVillageFilter}
                  onChange={setIssueVillageFilter}
                  options={issueVillageSelectOptions}
                  placeholder="All villages"
                />
              </label>
              {issueZoneGroupSelectOptions.length > 1 && (
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Group</span>
                  <SearchableSelect
                    id="issue-beneficiary-zone-group"
                    value={issueZoneGroupFilter}
                    onChange={setIssueZoneGroupFilter}
                    options={issueZoneGroupSelectOptions}
                    placeholder="All groups"
                  />
                </label>
              )}
            </div>
            <p className="text-muted" style={{ margin: "0", fontSize: "0.84rem" }}>
              Showing {filteredIssueBeneficiaries.length} of {eligibleApplications.length} beneficiaries
            </p>
          </div>
          <label className="inv-outward-issue__field inv-outward-issue__field--r1">
            <span className="inv-outward-issue__label">Step 1 — Beneficiary</span>
            <SearchableSelect
              id="issue-beneficiary-id"
              value={applicationId}
              onChange={setApplicationId}
              options={issueBeneficiarySelectOptions}
              placeholder="Select…"
            />
          </label>
          <label className="inv-outward-issue__field inv-outward-issue__field--r1">
            <span className="inv-outward-issue__label">Step 2 — Milestone</span>
            <SearchableSelect
              id="issue-milestone-template"
              value={milestoneTemplateId}
              onChange={setMilestoneTemplateId}
              options={poMilestoneSelectOptions}
              placeholder="Select…"
            />
          </label>
          <label className="inv-outward-issue__field inv-outward-issue__field--r1">
            <span className="inv-outward-issue__label">Step 2 — Selection mode</span>
            <SearchableSelect
              id="issue-selection-mode"
              value={selectionMode}
              onChange={(v) => setSelectionMode(v === "selected" ? "selected" : "all")}
              options={issueModeSelectOptions}
              placeholder="Select mode…"
            />
          </label>
          {selectionMode === "selected" ? (
            <div className="inv-outward-issue__field inv-outward-issue__field--full">
              <span className="inv-outward-issue__label">Standard resources (linked to milestone)</span>
              <div className="milestone-timeline-check-grid" role="group" aria-label="Pick standard resources">
                {linkedResources.map((r) => (
                  <label key={r.id} className="milestone-timeline-check">
                    <input
                      type="checkbox"
                      checked={selectedResourceIds.includes(r.id)}
                      onChange={() =>
                        setSelectedResourceIds((prev) =>
                          prev.includes(r.id) ? prev.filter((id) => id !== r.id) : [...prev, r.id],
                        )
                      }
                    />
                    <span className="milestone-timeline-check__text milestone-timeline-check__text--single">
                      <span className="milestone-timeline-check__title">{r.title}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <label className="inv-outward-issue__field inv-outward-issue__field--full">
            <span className="inv-outward-issue__label">Notes (optional)</span>
            <textarea
              className="form-control inv-outward-issue__notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any context for this generated issue set…"
            />
          </label>
        </div>
        {!selectedMilestone ? null : linkedResources.length === 0 ? (
          <p className="inv-outward-issue__hint">This milestone has no linked standard resources in master.</p>
        ) : (
          <p className="inv-outward-issue__hint">
            {selectionMode === "all"
              ? `Will include all ${linkedResources.length} linked resources for this milestone.`
              : `${selectedResourceIds.length} resource(s) selected.`}
          </p>
        )}
        <div className="inv-outward-issue__actions">
          {manualIssueGenerateState.showMessage ? (
            <p className="inv-outward-issue__hint" style={{ margin: "0 0 0.75rem", flex: "1 1 100%" }}>
              {manualIssueGenerateState.message}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn--primary btn--tg btn--lg"
            disabled={generating || !manualIssueGenerateState.canGenerate || previewItems.length === 0}
            onClick={() => void handleGenerate()}
          >
            {generating ? "Generating…" : "Generate issue rows"}
          </button>
        </div>

        <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
          Preview from selected resources
        </h3>
        {previewItems.length === 0 ? (
          <p className="text-muted">Select milestone/resources to preview item rows.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Item</th>
                  <th>Item master code</th>
                  <th>HSN/SAC</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Rate (₹)</th>
                  <th>Line (₹)</th>
                  <th>Issue status</th>
                </tr>
              </thead>
              <tbody>
                {previewItems.map((it) => {
                  const lineState = issueLineState(
                    applicationId.trim() && milestoneTemplateId.trim()
                      ? issueRowByKey.get(
                          issueRowLookupKey(applicationId.trim(), milestoneTemplateId.trim(), it.itemId),
                        )
                      : undefined,
                  );
                  return (
                  <tr key={`${it.resourceId}-${it.itemId}`}>
                    <td>{it.resourceTitle}</td>
                    <td>{it.itemLabel}</td>
                    <td className="mono">{it.catalogStandardItemId || "— missing mapping —"}</td>
                    <td className="mono">{it.hsnCode?.trim() || "—"}</td>
                    <td>{it.qty}</td>
                    <td>{it.unit}</td>
                    <td className="mono">{fmtInrAmount(it.rate ?? 0)}</td>
                    <td className="mono">
                      {fmtInrAmount(Math.round(it.qty * (it.rate ?? 0) * 100) / 100)}
                    </td>
                    <td>{issueLineStateLabel(lineState)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}
      </div>
      ) : null}

      {isIssueSection && issueViewMode === "register" ? (
      <div className="card card--gov card--elevated">
        <div className="po-analytics-header">
          <h3 className="form-section-title" style={{ marginTop: 0, marginBottom: 0 }}>
            Material analytics
          </h3>
          <button
            type="button"
            className="btn btn--sm btn--tg-secondary"
            onClick={() => {
              void loadIssueAnalytics();
              if (canUsePo) void loadPoAnalytics();
            }}
            disabled={issueAnalyticsLoading}
          >
            {issueAnalyticsLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {issueAnalyticsError ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {issueAnalyticsError}
          </p>
        ) : null}

        {canUsePo ? (
          <>
            <h4 className="form-section-title" style={{ fontSize: "0.95rem" }}>
              PO coverage
            </h4>
            <div className="kpi-grid" style={{ marginBottom: "1rem" }}>
              <div className="kpi-card kpi-card--pastel">
                <div className="kpi-card__label">Beneficiaries with PO</div>
                <div className="kpi-card__value">{poAnalyticsData?.totals.uniqueBeneficiaries ?? "—"}</div>
              </div>
              <div className="kpi-card kpi-card--pastel">
                <div className="kpi-card__label">Full POs</div>
                <div className="kpi-card__value">{poAnalyticsData?.totals.fullPo ?? "—"}</div>
              </div>
              <div className="kpi-card kpi-card--pastel">
                <div className="kpi-card__label">Partial POs</div>
                <div className="kpi-card__value">{poAnalyticsData?.totals.partialPo ?? "—"}</div>
              </div>
            </div>
          </>
        ) : null}

        <h4 className="form-section-title" style={{ fontSize: "0.95rem" }}>
          Material issue status
        </h4>
        <div className="kpi-grid kpi-grid--cols-4" style={{ marginBottom: "1rem" }}>
          <div className="kpi-card kpi-card--pastel">
            <div className="kpi-card__label">Beneficiaries (with issue rows)</div>
            <div className="kpi-card__value">{issueAnalyticsData?.totals.uniqueBeneficiaries ?? "—"}</div>
          </div>
          <div className="kpi-card kpi-card--pastel">
            <div className="kpi-card__label">Issue complete (beneficiary)</div>
            <div className="kpi-card__value">{issueAnalyticsData?.totals.beneficiariesCompleted ?? "—"}</div>
          </div>
          <div className="kpi-card kpi-card--pastel">
            <div className="kpi-card__label">Issue partial (beneficiary)</div>
            <div className="kpi-card__value">{issueAnalyticsData?.totals.beneficiariesPartial ?? "—"}</div>
          </div>
          <div className="kpi-card kpi-card--pastel">
            <div className="kpi-card__label">Issue pending (beneficiary)</div>
            <div className="kpi-card__value">{issueAnalyticsData?.totals.beneficiariesPending ?? "—"}</div>
          </div>
        </div>

        <h3 className="form-section-title">Issue breakdown by milestone</h3>
        {issueAnalyticsLoading && !issueAnalyticsData ? (
          <p className="text-muted">Loading…</p>
        ) : !issueAnalyticsData || issueAnalyticsData.byMilestone.length === 0 ? (
          <p className="text-muted">No data.</p>
        ) : (
          <div className="issue-analytics-table-scroll">
            <table className="data-table issue-analytics-breakdown-table">
              <colgroup>
                <col className="issue-analytics-breakdown-table__label" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
              </colgroup>
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Beneficiaries</th>
                  <th>Pending</th>
                  <th>Partial</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {issueAnalyticsData.byMilestone.map((m) => (
                  <tr key={m.milestoneTemplateId || m.milestoneTitle}>
                    <td>{m.milestoneTitle}</td>
                    <td>{m.uniqueBeneficiaries}</td>
                    <td>{m.pendingRows}</td>
                    <td>{m.partialRows}</td>
                    <td>{m.completedRows}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h3 className="form-section-title">Issue breakdown by subcontractor</h3>
        {issueAnalyticsLoading && !issueAnalyticsData ? (
          <p className="text-muted">Loading…</p>
        ) : !issueAnalyticsData || issueAnalyticsData.bySubcontractor.length === 0 ? (
          <p className="text-muted">No data.</p>
        ) : (
          <div className="issue-analytics-table-scroll">
            <table className="data-table issue-analytics-breakdown-table">
              <colgroup>
                <col className="issue-analytics-breakdown-table__label" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
                <col className="issue-analytics-breakdown-table__metric" />
              </colgroup>
              <thead>
                <tr>
                  <th>Subcontractor</th>
                  <th>Beneficiaries</th>
                  <th>Pending</th>
                  <th>Partial</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {issueAnalyticsData.bySubcontractor.map((s) => (
                  <tr key={s.subcontractorMasterId || s.subcontractorFirmName}>
                    <td>{s.subcontractorFirmName}</td>
                    <td>{s.uniqueBeneficiaries}</td>
                    <td>{s.pendingRows}</td>
                    <td>{s.partialRows}</td>
                    <td>{s.completedRows}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
          Issue register
        </h3>
        <div className="goods-receipts-filters" style={{ marginBottom: "1rem" }}>
          <div className="goods-receipts-filters__grid">
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Status</span>
              <select
                className="form-control"
                value={filterStatus}
                onChange={(e) => {
                  const next = e.target.value;
                  setFilterStatus(next === "pending" || next === "partial" || next === "completed" ? next : "all");
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search</span>
              <input
                type="search"
                className="form-control"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Beneficiary, subcontractor, milestone, item..."
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Sort</span>
              <select className="form-control" value={sortDir} onChange={(e) => setSortDir(e.target.value === "asc" ? "asc" : "desc")}>
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </label>
          </div>
        </div>
        {loadingRows ? (
          <p className="text-muted">Loading…</p>
        ) : rowsError ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {rowsError}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted">No issue rows found.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Beneficiary</th>
                  <th>Subcontractor</th>
                  <th>Milestone</th>
                  <th>Resource item</th>
                  <th>Mapped item</th>
                  <th>Rate (₹)</th>
                  <th>Planned</th>
                  <th>Issued</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="table-name">{row.beneficiaryName ?? "—"}</div>
                      <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                        {row.beneficiaryMobileApplicationId ?? row.applicationId}
                      </div>
                    </td>
                    <td>{row.subcontractorFirmName}</td>
                    <td>{row.milestoneTitle}</td>
                    <td>
                      <div>{row.resourceItemLabel}</div>
                      <div className="text-muted" style={{ fontSize: "0.85em" }}>
                        {row.standardResourceTitle}
                      </div>
                    </td>
                    <td>
                      <div>{row.catalogStandardItemId || "—"}</div>
                      <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                        {row.catalogStandardItemId}
                      </div>
                    </td>
                    <td className="mono">{fmtInrAmount(row.resourceItemRate ?? 0)}</td>
                    <td>{row.plannedQty}</td>
                    <td>{row.issuedQty}</td>
                    <td>{row.balanceQty}</td>
                    <td style={{ textTransform: "capitalize" }}>{row.status}</td>
                    <td className="text-muted">{formatWhen(row.updatedAt || row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : null}
    </div>
  );
}
